import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  makeStyles,
  createTheme,
  ThemeProvider,
  Typography,
  Box,
} from '@material-ui/core';
import { CommonColor } from '../../constants/Colors';
import { rankingListItem } from '../../models/RankingModel';
import { User } from '../../models/UserModel';

/**
 * 金額（円）を千円単位の表示文字列に変換する
 * - APIは数値を文字列で返すことがあるため Number() で正規化
 * - null/空文字/非数値で NaN にならないよう Number.isFinite でガード
 */
const formatThousands = (value: number | string | null | undefined): string => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.floor(num / 1000).toLocaleString() : '0';
};

/**
 * Props
 * @param rankList 請求者元名ごとのリスト
 * @param observerRef スクロール監視の要素取得用
 * @param parentDisplayColumn 親コンポーネントの表示列
 * @param setDisplayColumn 親コンポーネントの表示列更新関数
 * @param rankingItemCount ランキングリストの総数
 */
interface Props {
  rankList: rankingListItem[];
  observerRef: React.MutableRefObject<HTMLTableRowElement | null>;
  parentDisplayColumn: string;
  parentSetDisplayColumn: React.Dispatch<React.SetStateAction<string>>;
  rankingItemCount: number | null;
  isLoading: boolean;
  loginUserInfo: User | null;
}

const RankingList: React.FC<Props> = ({
  rankList,
  observerRef,
  parentDisplayColumn,
  parentSetDisplayColumn,
  rankingItemCount,
  isLoading,
  loginUserInfo,
}) => {
  const classes = useStyles();

  /************************************************
   * useState
   * *********************************************
   */

  /** 表示列の状態（初期：受領済） */
  const [displayColumn, setDisplayColumn] = useState('receipt');

  /** 順位列の状態（初期：順位） */
  const [rankingColumn, setRankingColumn] = useState('rankingNumber');

  /************************************************
   * function
   * *********************************************
   */

  /** displayColumnが変更された時に親コンポーネントのselectedColumnを更新 */
  const handleColumnChange = (newColumn: string) => {
    setDisplayColumn(newColumn);
    parentSetDisplayColumn(newColumn);
  };

  /**表示操作****************************************/

  /** 表示列の切り替え（受領：total/予測：receipt） */
  const handleChangeDisplayList = (column: string) => {
    handleColumnChange(column);
  };

  /** 順位列の切り替え（順位：rankingNumber/受領金額：totalReceived） */
  const handleChangeRankingList = (column: string) => {
    setRankingColumn(column);
  };

  /** 自身のIDがリスト内に存在するかどうか */
  const isMyself = rankList.find(item => item.user_id === loginUserInfo?.id);

  return (
    <ThemeProvider theme={customTheme}>
      <Container className={classes.tableWrapper}>
        <Typography className={classes.tableCellUnit}>単位：千円</Typography>
        <TableContainer
          component={Paper}
          className={rankList.length === 0 ? '' : classes.tableContainer}
        >
          <Table stickyHeader className={classes.table}>
            <TableHead className={classes.stickyHeader}>
              <TableRow>
                <TableCell colSpan={4} className={classes.mergedCell}>
                  <Typography>
                    累計{' '}
                    {!isLoading && isMyself !== undefined ? isMyself.current_rankingNumber : ' － '}
                    位 / {rankList.length === 0 ? ' － ' : rankingItemCount}人中
                  </Typography>
                  {!isLoading && isMyself === undefined ? (
                    <Typography className={classes.mergedCellErrorMessage}>
                      ※部門設定がされていません。本部画面【ユーザー管理】から設定してください。
                    </Typography>
                  ) : (
                    ''
                  )}
                </TableCell>
                <TableCell colSpan={2} className={classes.rightMergedCell}>
                  <Button
                    variant="contained"
                    color="default"
                    size="small"
                    onClick={() =>
                      handleChangeRankingList(
                        rankingColumn === 'rankingNumber' ? 'totalReceived' : 'rankingNumber'
                      )
                    }
                  >
                    {rankingColumn === 'rankingNumber' ? '順位' : '金額'}
                  </Button>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>順位</TableCell>
                <TableCell>請求者元名</TableCell>
                <TableCell
                  style={
                    displayColumn === 'receipt' ? { backgroundColor: CommonColor.Light.orange } : {}
                  }
                >
                  <Button
                    variant={displayColumn === 'receipt' ? 'contained' : 'outlined'}
                    color="default"
                    className={
                      displayColumn === 'receipt' ? classes.tableButtonActive : classes.tableButton
                    }
                    size="small"
                    onClick={() => handleChangeDisplayList('receipt')}
                    disabled={rankList.length === 0}
                  >
                    受領
                  </Button>
                </TableCell>
                <TableCell
                  className={classes.lastCell}
                  style={
                    displayColumn === 'total' ? { backgroundColor: CommonColor.Light.orange } : {}
                  }
                >
                  <Button
                    variant={displayColumn === 'total' ? 'contained' : 'outlined'}
                    color="default"
                    className={
                      displayColumn === 'total' ? classes.tableButtonActive : classes.tableButton
                    }
                    size="small"
                    onClick={() => handleChangeDisplayList('total')}
                    disabled={rankList.length === 0}
                  >
                    予測
                  </Button>
                </TableCell>
                <TableCell className={classes.titleRank}>前月</TableCell>
                {/* 前年＝前年同月 */}
                <TableCell className={classes.titleRank}>前年</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* ランキングリスト */}
              {rankList &&
                rankList.map((item: rankingListItem, index: number) => (
                  <TableRow
                    key={item.organisation_id}
                    className={item.user_id === loginUserInfo?.id ? classes.loginUser : ''}
                  >
                    <TableCell>{item.current_rankingNumber + '位'}</TableCell>
                    <TableCell>{item.organisation_name}</TableCell>
                    <TableCell
                      style={{
                        fontWeight: 'bold',
                        ...(displayColumn === 'receipt'
                          ? { backgroundColor: CommonColor.Light.orange }
                          : {}),
                      }}
                    >
                      {formatThousands(item.current_totalReceived)}
                    </TableCell>
                    <TableCell
                      className={classes.lastCell}
                      style={{
                        fontWeight: 'bold',
                        ...(displayColumn === 'total'
                          ? { backgroundColor: CommonColor.Light.orange }
                          : {}),
                      }}
                    >
                      {formatThousands(item.current_totalForecasted)}
                    </TableCell>
                    <TableCell className={classes.bodyRank}>
                      {rankingColumn === 'rankingNumber'
                        ? item.current_rankingNumber_prevMonth + '位'
                        : formatThousands(item.current_totalReceived_prevMonth)}
                    </TableCell>
                    <TableCell className={classes.bodyRank}>
                      {rankingColumn === 'rankingNumber'
                        ? item.current_rankingNumber_prevYearMonth + '位'
                        : formatThousands(item.current_totalReceived_prevYearMonth)}
                    </TableCell>
                  </TableRow>
                ))}
              {/* スクロール終端の監視用要素（到達時点でリストを追加） */}
              {rankingItemCount !== null &&
                rankingItemCount >= 15 &&
                rankingItemCount !== rankList.length && (
                  <tr ref={observerRef} style={{ height: '20px' }} />
                )}
            </TableBody>
          </Table>
        </TableContainer>
        {!isLoading && rankList.length === 0 && (
          <Box className={classes.notDataMassageWrapper}>
            <Typography className={classes.notDataMassage}>ランキングデータがありません</Typography>
            <Typography className={classes.notDataMassage}>
              【ユーザ管理】にて各ユーザの部門設定がされているか
              <br className={classes.notPC} />
              確認してください。
            </Typography>
            {/* 本部No103 個人部門設定の概念が無くなったのでコメントアウト */}
            {/* <Typography className={classes.notDataMassage}>
              本部画面にて【個人部門】設定がされているか
              <br className={classes.notPC} />
              確認してください。
            </Typography> */}
          </Box>
        )}
      </Container>
    </ThemeProvider>
  );
};

/************************************************
 * style
 * *********************************************
 */

// カスタム
const useStyles = makeStyles({
  tableWrapper: {
    marginTop: '10px',
    padding: '0',
    height: '100%',
  },
  tableCellUnit: {
    fontSize: '0.8rem',
    textAlign: 'right',
  },
  tableContainer: {
    height: '95%',
  },
  tableButton: {
    padding: '5px',
    lineHeight: '1.5',
    width: '100%',
  },
  tableButtonActive: {
    width: '100%',
    backgroundColor: CommonColor.Normal.orange,
    color: '#fafafa',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: CommonColor.Normal.orange,
    },
  },
  table: {
    borderCollapse: 'collapse',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    backgroundColor: CommonColor.Light.orange,
    zIndex: 2,
  },
  mergedCell: {
    backgroundColor: CommonColor.Light.orange,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    borderRight: '1px solid #ccc',
  },
  mergedCellErrorMessage: {
    color: CommonColor.Normal.Red,
    fontSize: '0.8rem',
  },
  rightMergedCell: {
    textAlign: 'center',
    fontSize: '0.9rem',
  },
  titleRank: {
    lineHeight: '1',
    fontSize: 'min(12px,0.8rem)',
    '@media (min-width: 600px)': {
      fontSize: '15px',
    },
  },
  lastCell: {
    borderRight: '1px solid #ccc',
  },
  bodyRank: {
    fontSize: 'min(12px,0.8rem)',
    '@media (min-width: 600px)': {
      fontSize: '15px',
    },
  },
  notDataMassageWrapper: {
    marginTop: '15px',
    textAlign: 'center',
    display: 'grid',
    placeItems: 'center',
    gap: '5px',
  },
  notDataMassage: {
    fontSize: '0.8rem',
  },
  notPC: {
    display: 'block',
    '@media (min-width: 600px)': {
      display: 'none',
    },
  },
  loginUser: {
    border: `2px solid ${CommonColor.Normal.Red}`,
    '@media (min-width: 600px)': {
      border: `3px solid ${CommonColor.Normal.Red}`,
    },
  },
});

// Material-UIのテーマカスタマイズ
const customTheme = createTheme({
  overrides: {
    MuiTableCell: {
      root: {
        padding: '10px 2px',
        textAlign: 'center',
      },
      head: {
        padding: '5px 2px',
      },
    },
  },
});

export default RankingList;
